import { useState } from 'react';
import { ArrowClockwise, CheckCircle, MinusCircle, WarningCircle, XCircle } from '@phosphor-icons/react';
import { ConfirmationModal } from '@/components/core';
import { cn } from '@/lib/utils';

export type CampaignReviewCheckSeverity = 'blocking' | 'advisory' | 'deferred';

export type CampaignReviewCheck = {
  id: string;
  label: string;
  pass: boolean;
  detail?: string;
  severity: CampaignReviewCheckSeverity;
  milestone?: string;
  fixStep?: 1 | 2 | 3 | 4;
};

const CHECK_FIX_LABEL: Record<string, string> = {
  content: 'Go to Content',
  plaintext: 'Go to Content',
  subject: 'Go to Details',
  sender: 'Go to Details',
  audience: 'Go to Audience',
  sendable: 'Go to Audience',
};

type CampaignReviewStepProps = {
  subject: string;
  fromName: string;
  fromEmail: string;
  audienceLabel: string;
  contentLabel: string;
  sendingLabel: string;
  checks: CampaignReviewCheck[];
  onRefreshChecks: () => void;
  onFixCheck: (step: 1 | 2 | 3 | 4) => void;
};

export const CampaignReviewStep = ({
  subject,
  fromName,
  fromEmail,
  audienceLabel,
  contentLabel,
  sendingLabel,
  checks,
  onRefreshChecks,
  onFixCheck,
}: CampaignReviewStepProps) => {
  const [activeCheck, setActiveCheck] = useState<CampaignReviewCheck | null>(null);

  const fromDisplay =
    fromName.trim() || fromEmail.trim()
      ? `${fromName.trim() || '—'} <${fromEmail.trim() || '—'}>`
      : '—';

  const fixLabel = activeCheck
    ? CHECK_FIX_LABEL[activeCheck.id] || 'Go fix'
    : 'Go fix';

  return (
    <>
      <div className="flex flex-col gap-4 rounded-lg border border-neutral-200 bg-white p-6 shadow-e1">
        <div className="flex items-center justify-between gap-3">
          <h2 className="text-base font-semibold leading-6 text-neutral-900">Summary</h2>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 text-[13px] font-medium leading-[18px] text-primary-600 hover:text-primary-700"
            onClick={onRefreshChecks}
          >
            <ArrowClockwise className="size-3.5" aria-hidden />
            Refresh checks
          </button>
        </div>

        <dl className="grid grid-cols-3 gap-x-6 gap-y-3 text-sm leading-5">
          <dt className="text-neutral-500">Subject</dt>
          <dd className="col-span-2 text-neutral-900">{subject.trim() || '—'}</dd>

          <dt className="text-neutral-500">From</dt>
          <dd className="col-span-2 text-neutral-900">{fromDisplay}</dd>

          <dt className="text-neutral-500">Audience</dt>
          <dd className="col-span-2 text-neutral-900">{audienceLabel}</dd>

          <dt className="text-neutral-500">Content</dt>
          <dd className="col-span-2 text-neutral-900">{contentLabel}</dd>

          <dt className="text-neutral-500">Sending</dt>
          <dd className="col-span-2 text-neutral-900">{sendingLabel}</dd>
        </dl>

        <div className="border-t border-neutral-200 pt-3">
          <div className="mb-2 text-[12px] font-medium uppercase leading-4 tracking-wide text-neutral-500">
            Pre-send checks
          </div>
          <div className="flex flex-col gap-1.5">
            {checks.map((check) => (
              <ReviewCheckRow
                key={check.id}
                check={check}
                onActivate={() => setActiveCheck(check)}
              />
            ))}
          </div>
        </div>
      </div>

      <ConfirmationModal
        open={Boolean(activeCheck)}
        onOpenChange={(open) => {
          if (!open) setActiveCheck(null);
        }}
        title={activeCheck?.label || 'Check'}
        description={
          <div className="space-y-2">
            <p>{activeCheck?.detail || activeCheck?.label}</p>
            {activeCheck ? (
              <p className="text-[13px] text-neutral-500">
                Code: <code>{activeCheck.id}</code>
              </p>
            ) : null}
          </div>
        }
        confirmText={fixLabel}
        cancelText="Close"
        confirmClassName="bg-primary-600 hover:bg-primary-700 text-white"
        onConfirm={() => {
          if (activeCheck?.fixStep) onFixCheck(activeCheck.fixStep);
          setActiveCheck(null);
        }}
      />
    </>
  );
};

const ReviewCheckRow = ({
  check,
  onActivate,
}: {
  check: CampaignReviewCheck;
  onActivate: () => void;
}) => {
  if (check.severity === 'deferred') {
    return (
      <div
        className="flex items-center gap-2.5 rounded-md px-2.5 py-2 opacity-60"
        title={check.detail}
      >
        <MinusCircle className="size-[18px] shrink-0 text-neutral-500" weight="regular" aria-hidden />
        <span className="flex-1 text-neutral-500">{check.label}</span>
        <span className="rounded-full bg-neutral-100 px-2 py-0.5 text-[12px] font-medium text-neutral-700">
          {check.milestone || 'Later'}
        </span>
      </div>
    );
  }

  const clickable = !check.pass && Boolean(check.fixStep);
  const icon = check.pass ? (
    <CheckCircle className="size-[18px] shrink-0 text-[#1F8A46]" weight="fill" aria-hidden />
  ) : check.severity === 'blocking' ? (
    <XCircle className="size-[18px] shrink-0 text-[#D80032]" weight="fill" aria-hidden />
  ) : (
    <WarningCircle className="size-[18px] shrink-0 text-[#B4690E]" weight="fill" aria-hidden />
  );

  return (
    <div
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      className={cn(
        'flex items-center gap-2.5 rounded-md px-2.5 py-2',
        clickable && 'cursor-pointer hover:bg-neutral-50'
      )}
      onClick={() => {
        if (clickable) onActivate();
      }}
      onKeyDown={(event) => {
        if (!clickable) return;
        if (event.key === 'Enter' || event.key === ' ') {
          event.preventDefault();
          onActivate();
        }
      }}
    >
      {icon}
      <span className={cn('flex-1 text-neutral-900', !check.pass && 'font-medium')}>
        {check.label}
      </span>
      {!check.pass ? (
        <span
          className={cn(
            'text-[13px] leading-[18px]',
            check.severity === 'blocking' ? 'text-error' : 'text-neutral-500'
          )}
        >
          {check.severity === 'blocking' ? 'Blocking' : 'Advisory'}
        </span>
      ) : null}
    </div>
  );
};
